// src/pages/container_rental/components/timeline/TimelineLegend.tsx
//
// 범례. 구 매니페스트의 mono 대문자 병기 라벨("LABEL · 한글")과 각진 먹 슬랩
// 타이틀을 걷어내고, 확정 디자인 시스템의 흰 카드 + 중립 칩 타이틀 +
// 한글 단독 라벨로 정돈했다. 스와치의 상태 의미색은 statusVisuals 경유 그대로.

import { Box } from "@mui/material";
import { designTokens } from "../../../../theme.ts";
import {
  MARK,
  RENTAL_VISUALS,
  ROUND_OVERDUE_VISUAL,
  ROUND_VISUALS,
} from "../statusVisuals.ts";

const T = designTokens;

const C = {
  surface: "#FFFFFF",
  border: "#E4E7EA",
  ink: T.ink,
  sub: "#6B7178",
  neutral: "#F2F4F5",
};

interface SwatchSpec {
  /** React key 전용 — 화면에는 한글 라벨만 노출한다. */
  key: string;
  ko: string;
  fill: string;
  stroke: string;
  dashed?: boolean;
  hatch?: string;
}

const ROUND_ITEMS: SwatchSpec[] = [
  {
    key: "scheduled",
    ko: ROUND_VISUALS.scheduled.ko,
    fill: T.paper,
    stroke: "#C6CBD0",
  },
  {
    // 면 채움은 RentalBar 가 그리는 세그먼트(ROUND_VISUALS.issued)를 따른다 —
    // 범례와 차트의 색이 갈라지지 않도록 단일 소스를 참조한다.
    key: "issued",
    ko: ROUND_VISUALS.issued.ko,
    fill: ROUND_VISUALS.issued.fill,
    stroke: ROUND_VISUALS.issued.accent,
  },
  {
    key: "skipped",
    ko: ROUND_VISUALS.skipped.ko,
    fill: "#FCF3E4",
    stroke: T.amber,
    hatch: T.amber,
  },
  {
    key: "cancelled",
    ko: ROUND_VISUALS.cancelled.ko,
    fill: "#FBEDED",
    stroke: T.stop,
    hatch: T.stop,
  },
  {
    key: "overdue",
    ko: ROUND_OVERDUE_VISUAL.ko,
    fill: ROUND_OVERDUE_VISUAL.fill,
    stroke: T.stop,
  },
];

const RENTAL_ITEMS: SwatchSpec[] = (
  ["active", "paused", "draft", "closed", "cancelled"] as const
).map((s) => {
  const v = RENTAL_VISUALS[s];
  return {
    key: s,
    ko: v.ko,
    fill: v.accent,
    stroke: v.accent,
    dashed: Boolean(v.frameDash),
  };
});

export default function TimelineLegend() {
  return (
    <Box
      sx={{
        mt: 1.5,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        bgcolor: C.surface,
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        px: 1.75,
        py: 1.25,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: { xs: 1, md: 1.5 },
      }}
    >
      <LegendTitle>회차</LegendTitle>
      {ROUND_ITEMS.map((it) => (
        <LegendItem key={`r-${it.key}`} item={it} />
      ))}

      <Divider />

      <LegendTitle>계약</LegendTitle>
      {RENTAL_ITEMS.map((it) => (
        <LegendItem key={`c-${it.key}`} item={it} />
      ))}

      <Divider />

      <LegendTitle>표식</LegendTitle>
      <MarkItem
        label="오늘"
        node={<Box sx={{ width: 2, height: 14, bgcolor: MARK.today, borderRadius: "1px" }} />}
      />
      <MarkItem
        label="1회차"
        node={
          <Box
            sx={{
              width: 14,
              height: 12,
              bgcolor: T.paper,
              borderRadius: "3px",
              border: "1px solid #C6CBD0",
              borderBottom: `3px solid ${MARK.init}`,
            }}
          />
        }
      />
      <MarkItem
        label="발행 예정"
        node={
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: `7px solid ${MARK.issueTick}`,
            }}
          />
        }
      />
      <MarkItem
        label="만료임박 D-90"
        node={
          <Box
            sx={{
              width: 6,
              height: 14,
              bgcolor: MARK.expiring,
              borderRadius: "2px",
            }}
          />
        }
      />
    </Box>
  );
}

function Divider() {
  return (
    <Box
      sx={{
        width: "1px",
        alignSelf: "stretch",
        bgcolor: C.border,
        mx: 0.5,
        display: { xs: "none", md: "block" },
      }}
    />
  );
}

function LegendTitle({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: C.neutral,
        color: C.sub,
        px: 0.9,
        py: 0.3,
        borderRadius: "6px",
        fontFamily: T.fonts.sans,
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

function LegendItem({ item }: { item: SwatchSpec }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 14,
          height: 12,
          borderRadius: "3px",
          bgcolor: item.fill,
          border: `${item.dashed ? "1px dashed" : "1px solid"} ${item.stroke}`,
          backgroundImage: item.hatch
            ? `repeating-linear-gradient(45deg, ${item.hatch} 0 2px, transparent 2px 5px)`
            : "none",
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 12,
          color: C.ink,
          whiteSpace: "nowrap",
        }}
      >
        {item.ko}
      </Box>
    </Box>
  );
}

function MarkItem({ label, node }: { label: string; node: React.ReactNode }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14 }}>
        {node}
      </Box>
      <Box
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 12,
          color: C.ink,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
  );
}
